<template>
  <div>
    <Header :self="self" />
    <div class="container main">
      <div class="alert alert-danger" role="alert" v-if="error">
        {{error.message}}
      </div>

      <h4>Join Quozio</h4>
      <p>Save your favorite quotes, create public and private quote boards, follow others and more. It's free! You won't regret it.</p>

      <div class="text-center" v-if="step == 1">
        <div class="mb-4">
          <a href="/connect/facebook"><span class="btn btn-lg btn-facebook"><i class="fab fa-facebook"></i> Connect with Facebook</span></a>
        </div>
        <div class="mb-4">
          Or, you can sign up using your email address
        </div>
      </div>

      <form @submit.prevent="createUser" :disabled="submitting.signIn">
        <div class="form-group row" v-bind:class="{ 'form-group-error': $v.user.email.$error }" v-show="step == 1 || (error && error.code)">
          <label for="email" class="col-sm-2 col-form-label">Email</label>
          <div class="col-sm-10">
            <input type="text" class="form-control" id="email" v-model="user.email" v-model.trim.lazy="$v.user.email.$model">
            <div class="error-input">Please provide your email address</div>
          </div>
        </div>
        <div class="form-group row" v-bind:class="{ 'form-group-error': $v.user.password.$error }" v-show="step == 1 || (error && error.code)">
          <label for="password" class="col-sm-2 col-form-label">Password</label>
          <div class="col-sm-10">
            <input type="password" class="form-control" id="password" v-model="user.password" v-model.trim.lazy="$v.user.password.$model">
            <div class="error-input">Please choose a password. At least 6 characters.</div>
          </div>
        </div>
        <div class="alert alert-primary" role="alert" v-if="step == 2 && !error">
          <strong>Great!</strong> We need a bit more info to complete your profile.
        </div>
        <div class="form-group row" v-bind:class="{ 'form-group-error': $v.user.username.$error }" v-show="step == 2 || (error && error.code)">
          <label for="username" class="col-sm-2 col-form-label">Username</label>
          <div class="col-sm-10">
            <input type="text" class="form-control" id="username" v-model="user.username" v-model.trim.lazy="$v.user.username.$model">
            <div class="error-input">Please choose a username</div>
            <small class="form-text text-muted">This will be used for your profile page. At least 5 characters.</small>
            <small class="form-text text-muted" v-if="user.username && user.username.length >= 5">Your page: <strong class="text-monospace text-primary">https://quozio.com/{{user.username}}</strong></small>
          </div>
        </div>
        <div class="form-group row" v-bind:class="{ 'form-group-error': $v.user.name.$error }" v-show="step == 2 || (error && error.code)">
          <label for="name" class="col-sm-2 col-form-label">Your Name</label>
          <div class="col-sm-10">
            <input type="text" class="form-control" id="name" v-model="user.name" v-model.trim.lazy="$v.user.name.$model">
            <div class="error-input">Please provide your name</div>
            <small class="form-text text-muted">This will be displayed with your public profile. At least 3 characters.</small>
          </div>
        </div>
        <div class="text-right">
          <button class="btn btn-primary" :disabled="submitting.signIn" v-if="step == 1">Continue</button>
          <button class="btn btn-primary" :disabled="submitting.signIn" v-if="step == 2">Finish</button>
        </div>
      </form>
      <div class="my-2">
        <span class="faux-link" @click="signIn()">Already have an account? Sign in</span>
      </div>
    </div>
    <Footer />
  </div>
</template>

<script>
  import Header from "@/components/header";
  import Footer from "@/components/footer";
  import { required } from "vuelidate/lib/validators";
  import { mapActions, mapMutations, mapState } from "vuex";

  export default {
    components: { Header, Footer },
    data() {
      return {
        user: { name: null, email: null, username: null, password: null },
        step: 1,
        error: null,
        submitting: { signIn: false, createCheckoutSession: false },
      };
    },
    head() {
      return {
        title: "Quozio - Create Your Account",
        meta: [
          { hid: "description", name: "description", content: "Join Quozio to create, save and share your favorite quotes" }
        ],
        script: [
          {
            hid: "stripe",
            src: "https://js.stripe.com/v3/",
            defer: true,
            callback: () => { this.isStripeLoaded = true }
          }
        ]
      }
    },
    async asyncData({ store, app }) {
      await store.dispatch("self/get", app.$cookies);
    },
    methods: {
      async createUser() {
        this.error = null;

        if (this.step == 1) {

          if (!this.user.email || !this.user.password) {
            this.error = { message: "Please complete the required fields." };
            return;
          }

          this.$ga.event("User", "User Created - Start", "step1");
          this.step = 2;
          return;
        }

        this.$v.$touch();
        if (this.$v.$invalid) {
          return;
        }

        this.submitting.signIn = true;
        this.$nuxt.$loading.start();
        let response = await this.$dataService.post("/users", this.user);
        if (response.error) {
          // Only re-enable the submit button if an error.
          this.submitting.signIn = false;
          this.error = response.error;
          this.$nuxt.$loading.finish();
          return;
        }

        this.setAuthToken({ cookies: this.$cookies, token: response.data.token });
        delete response.data.token;
        this.updateSelf(response.data);

        this.$nuxt.$loading.finish();

        this.$ga.event("User", "User Created", response.data.userId);

        // If the user joined as part of the process to upgrade to Quozio Pro, redirect them to the checkout page.
        if (this.$route.query.then == "get-quozio-pro") {
          await this.checkout();
          return;
        }

        // If a redirect, it must start with a forward slash, no exceptions.
        // Otherwise, someone could hijack the join page and redirect the user to an external URL in an attempt to phish.
        let redirect = this.$route.query.redirect;
        if (redirect && redirect.substring(0, 1) != "/") {
          redirect = null;
        }

        if (redirect) {
          this.$router.push(redirect);
        } else {
          this.$router.push("/");
        }
      },
      async checkout() {

        this.$ga.event("GetQuozioPro", "Get Quozio Pro - Load Checkout", 0);

        this.submitting.createCheckoutSession = true;
        this.$nuxt.$loading.start();
        let response = await this.$dataService.post("/payments/create-checkout-session", { meta: { ref: this.$route.query.ref } });
        this.$nuxt.$loading.finish();
        if (response.error) {
          this.error = response.error;
          this.submitting.createCheckoutSession = false;
          return;
        }

        // Redirect to the checkout page.
        var stripe = Stripe(this.$config.STRIPE_PUBLIC_API_KEY);
        stripe.redirectToCheckout({ sessionId: response.data.id });

      },
      signIn() {
        let query = {};
        if (this.$route.query.ref) {
          query.ref = this.$route.query.ref;
        }

        if (this.$route.query.then) {
          query.then = this.$route.query.then;
        }

        this.$router.push({ name: "sign-in", query: query });
      },
      ...mapActions({ setAuthToken: "self/setAuthToken" }),
      ...mapMutations({ updateSelf: "self/set" }),
    },
    computed: {
      ...mapState("self", { self: "self" }),
    },
    validations: {
      user: {
        name: {
          required
        },
        email: {
          required
        },
        username: {
          required
        },
        password: {
          required
        },
      }
    }
  };
</script>
